import { useState } from "react"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useNavigate } from "react-router-dom"
import {
  Activity,
  AlertCircle,
  ArrowRight,
  FolderOpen,
  Loader2,
  MoreVertical,
  Pencil,
  Plus,
  Settings2,
  Trash2,
} from "lucide-react"

import { Button } from "@/components/ui/button"
import { Input } from "@/components/ui/input"
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { apiRequest } from "../lib/api"
import type { ApiError } from "../lib/api"
import type { Project } from "../types"
import { cn } from "@/lib/utils"

type ProjectsResponse = {
  projects: Project[]
}

type ProjectsPageProps = {
  currentProject: Project | null
}

const ProjectsPage = ({ currentProject }: ProjectsPageProps) => {
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const [newName, setNewName] = useState("")
  const [isCreating, setIsCreating] = useState(false)
  
  const [editingSlug, setEditingSlug] = useState<string | null>(null)
  const [editingName, setEditingName] = useState("")
  const [editingThrottle, setEditingThrottle] = useState<string>("")
  const [editingFacebookPageId, setEditingFacebookPageId] = useState("")
  const [editingFacebookAccessToken, setEditingFacebookAccessToken] = useState("")
  const [editingFacebookConfigured, setEditingFacebookConfigured] = useState(false)

  const { data, isLoading, isError, error } = useQuery<ProjectsResponse, ApiError>({
    queryKey: ["projects"],
    queryFn: () => apiRequest<ProjectsResponse>("/api/projects"),
  })

  const projects = data?.projects ?? []

  const createMutation = useMutation<{ project: Project }, ApiError>({
    mutationFn: () =>
      apiRequest<{ project: Project }>("/api/projects", {
        method: "POST",
        body: JSON.stringify({ project: { name: newName } }),
      }),
    onSuccess: (payload) => {
      setNewName("")
      setIsCreating(false)
      queryClient.invalidateQueries({ queryKey: ["projects"] })
      queryClient.invalidateQueries({ queryKey: ["me"] })
      navigate(`/${payload.project.slug}`)
    },
  })

  const updateMutation = useMutation<{ project: Project }, ApiError, { slug: string }>({
    mutationFn: ({ slug }) => {
      const payload: {
        name: string;
        message_throttle_per_minute?: number | null;
        facebook_page_id?: string | null;
        facebook_page_access_token?: string;
      } = {
        name: editingName,
      }
      const throttle = editingThrottle.trim()
      payload.message_throttle_per_minute =
        throttle && /^\d+$/.test(throttle) && parseInt(throttle, 10) > 0
          ? parseInt(throttle, 10)
          : null
      payload.facebook_page_id = editingFacebookPageId.trim() || null
      if (editingFacebookAccessToken.trim()) {
        payload.facebook_page_access_token = editingFacebookAccessToken.trim()
      }
      return apiRequest<{ project: Project }>(`/api/projects/${slug}`, {
        method: "PATCH",
        body: JSON.stringify({ project: payload }),
      })
    },
    onSuccess: () => {
      setEditingSlug(null)
      setEditingName("")
      setEditingThrottle("")
      setEditingFacebookPageId("")
      setEditingFacebookAccessToken("")
      setEditingFacebookConfigured(false)
      queryClient.invalidateQueries({ queryKey: ["projects"] })
      queryClient.invalidateQueries({ queryKey: ["me"] })
    },
  })

  const deleteMutation = useMutation<void, ApiError, { slug: string }>({
    mutationFn: ({ slug }) =>
      apiRequest(`/api/projects/${slug}`, {
        method: "DELETE",
      }),
    onSuccess: (_, variables) => {
      const remaining = projects.filter((project) => project.slug !== variables.slug)
      queryClient.invalidateQueries({ queryKey: ["projects"] })
      queryClient.invalidateQueries({ queryKey: ["me"] })
      if (currentProject?.slug === variables.slug) {
        navigate(remaining[0] ? `/${remaining[0].slug}` : "/onboarding")
      }
    },
  })

  const handleCreate = (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!newName.trim()) return
    createMutation.mutate()
  }

  const startEditing = (project: Project) => {
    setEditingSlug(project.slug)
    setEditingName(project.name)
    setEditingThrottle(project.message_throttle_per_minute != null ? String(project.message_throttle_per_minute) : "")
    setEditingFacebookPageId(project.facebook_page_id ?? "")
    setEditingFacebookAccessToken("")
    setEditingFacebookConfigured(project.facebook_access_token_configured === true)
  }

  const handleUpdate = (e?: React.FormEvent) => {
    e?.preventDefault()
    if (!editingSlug) return
    if (!editingName.trim()) return
    updateMutation.mutate({ slug: editingSlug })
  }

  const handleDelete = (project: Project) => {
    const confirmed = window.confirm(`Tem certeza que deseja excluir o projeto "${project.name}"? Esta ação não pode ser desfeita.`)
    if (!confirmed) return
    deleteMutation.mutate({ slug: project.slug })
  }

  return (
    <div className="flex flex-col gap-8 pb-10">
      <div className="flex flex-col items-start justify-between gap-4 sm:flex-row sm:items-center">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Projetos</h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Gerencie seus espaços de trabalho e ambientes de transmissão.
          </p>
        </div>
        {!isCreating && (
          <Button onClick={() => setIsCreating(true)}>
            <Plus className="mr-2 h-4 w-4" />
            Novo Projeto
          </Button>
        )}
      </div>

      {isError ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-6 text-red-900 dark:border-red-900/50 dark:bg-red-950/20 dark:text-red-200">
          <div className="flex items-center gap-3">
            <AlertCircle className="h-5 w-5 text-red-600 dark:text-red-500" />
            <h3 className="font-semibold">Falha ao carregar projetos</h3>
          </div>
          <p className="mt-2 text-sm">{error?.message ?? "Ocorreu um erro inesperado."}</p>
        </div>
      ) : null}

      {deleteMutation.isError ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900 dark:border-red-900/50 dark:bg-red-950/20 dark:text-red-200">
          Falha ao excluir projeto: {deleteMutation.error.message}
        </div>
      ) : null}

      {updateMutation.isError ? (
        <div className="rounded-xl border border-red-200 bg-red-50 p-4 text-sm text-red-900 dark:border-red-900/50 dark:bg-red-950/20 dark:text-red-200">
          Falha ao atualizar projeto: {updateMutation.error.message}
        </div>
      ) : null}

      <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {/* Create New Project Card */}
        {isCreating && (
          <Card className="border-primary/50 shadow-xs ring-1 ring-primary/20">
            <form onSubmit={handleCreate}>
              <CardHeader>
                <CardTitle>Criar Novo Projeto</CardTitle>
                <CardDescription>Digite um nome para seu novo espaço de trabalho</CardDescription>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col gap-4">
                  <div className="space-y-2">
                    <Input
                      autoFocus
                      placeholder="ex.: Marketing, Vendas, App"
                      value={newName}
                      onChange={(event) => setNewName(event.target.value)}
                      disabled={createMutation.isPending}
                    />
                  </div>
                  {createMutation.isError ? (
                    <p className="text-sm text-destructive">{createMutation.error.message}</p>
                  ) : null}
                </div>
              </CardContent>
              <CardFooter className="flex justify-end gap-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => setIsCreating(false)}
                  disabled={createMutation.isPending}
                >
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  disabled={createMutation.isPending || !newName.trim()}
                >
                  {createMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Criar
                </Button>
              </CardFooter>
            </form>
          </Card>
        )}

        {isLoading ? (
          Array.from({ length: 3 }).map((_, i) => (
            <Card key={i} className="flex h-[200px] items-center justify-center">
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/50" />
            </Card>
          ))
        ) : projects.length === 0 && !isCreating ? (
          <div className="col-span-full flex min-h-[300px] flex-col items-center justify-center rounded-xl border border-dashed bg-muted/10 p-8 text-center">
            <FolderOpen className="mb-4 h-12 w-12 text-muted-foreground/30" />
            <h2 className="mb-2 text-xl font-semibold">Nenhum Projeto Encontrado</h2>
            <p className="mb-6 max-w-sm text-sm text-muted-foreground">
              Você ainda não criou nenhum projeto. Crie seu primeiro projeto para começar a transmitir.
            </p>
            <Button onClick={() => setIsCreating(true)}>
              <Plus className="mr-2 h-4 w-4" />
              Criar Projeto
            </Button>
          </div>
        ) : (
          projects.map((project) => {
            const isEditing = editingSlug === project.slug
            const isCurrent = currentProject?.slug === project.slug

            if (isEditing) {
              return (
                <Card key={project.slug} className="border-primary/50 shadow-xs ring-1 ring-primary/20">
                  <form onSubmit={handleUpdate}>
                    <CardHeader>
                      <CardTitle>Editar Projeto</CardTitle>
                      <CardDescription>Atualize as configurações do projeto</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <div className="flex flex-col gap-4">
                        <div className="space-y-1.5">
                          <label className="text-xs font-medium text-muted-foreground">Nome</label>
                          <Input
                            autoFocus
                            value={editingName}
                            onChange={(event) => setEditingName(event.target.value)}
                            disabled={updateMutation.isPending}
                          />
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-xs font-medium text-muted-foreground">Limite de taxa (msgs/min)</label>
                          <Input
                            type="number"
                            min={1}
                            placeholder="Ilimitado"
                            value={editingThrottle}
                            onChange={(event) => setEditingThrottle(event.target.value)}
                            disabled={updateMutation.isPending}
                          />
                          <p className="text-[10px] text-muted-foreground">Deixe vazio para sem limite</p>
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-xs font-medium text-muted-foreground">Facebook Page ID</label>
                          <Input
                            value={editingFacebookPageId}
                            onChange={(event) => setEditingFacebookPageId(event.target.value)}
                            disabled={updateMutation.isPending}
                            placeholder="123456789012345"
                          />
                        </div>
                        <div className="space-y-1.5">
                          <label className="text-xs font-medium text-muted-foreground">Page Access Token</label>
                          <Input
                            type="password"
                            value={editingFacebookAccessToken}
                            onChange={(event) => setEditingFacebookAccessToken(event.target.value)}
                            disabled={updateMutation.isPending}
                            placeholder={editingFacebookConfigured ? "Já configurado. Preencha para substituir." : "EAAB..."}
                          />
                          <p className="text-[10px] text-muted-foreground">
                            {editingFacebookConfigured
                              ? "Deixe em branco para manter o token atual."
                              : "O token é salvo de forma criptografada."}
                          </p>
                        </div>
                      </div>
                    </CardContent>
                    <CardFooter className="flex justify-end gap-2">
                      <Button
                        type="button"
                        variant="ghost"
                        onClick={() => setEditingSlug(null)}
                        disabled={updateMutation.isPending}
                      >
                        Cancelar
                      </Button>
                      <Button
                        type="submit"
                        disabled={updateMutation.isPending || !editingName.trim()}
                      >
                        {updateMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Salvar Alterações
                      </Button>
                    </CardFooter>
                  </form>
                </Card>
              )
            }

            return (
              <Card
                key={project.slug}
                className={cn(
                  "group flex flex-col transition-all hover:border-primary/30 hover:shadow-md",
                  isCurrent && "border-primary/30 bg-primary/5"
                )}
              >
                <CardHeader className="flex flex-row items-start justify-between space-y-0 pb-2">
                  <div className="space-y-1">
                    <CardTitle className="flex items-center gap-2">
                      <FolderOpen className={cn("h-5 w-5", isCurrent ? "text-primary" : "text-muted-foreground")} />
                      {project.name}
                      {isCurrent && (
                        <span className="rounded-full bg-primary/20 px-2 py-0.5 text-[10px] font-medium text-primary">
                          Atual
                        </span>
                      )}
                    </CardTitle>
                    <CardDescription className="font-mono text-xs">
                      /{project.slug}
                    </CardDescription>
                  </div>
                  <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" size="icon" className="h-8 w-8 opacity-0 group-hover:opacity-100 data-[state=open]:opacity-100">
                        <MoreVertical className="h-4 w-4" />
                        <span className="sr-only">Abrir menu</span>
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => navigate(`/${project.slug}`)}>
                        <ArrowRight className="mr-2 h-4 w-4" /> Abrir
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => navigate(`/${project.slug}/settings`)}>
                        <Settings2 className="mr-2 h-4 w-4" /> Configurações
                      </DropdownMenuItem>
                      <DropdownMenuItem onClick={() => startEditing(project)}>
                        <Pencil className="mr-2 h-4 w-4" /> Editar configurações
                      </DropdownMenuItem>
                      <DropdownMenuSeparator />
                      <DropdownMenuItem
                        className="text-destructive focus:bg-destructive/10 focus:text-destructive"
                        onClick={() => handleDelete(project)}
                        disabled={deleteMutation.isPending}
                      >
                        <Trash2 className="mr-2 h-4 w-4" /> Excluir
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </CardHeader>
                <CardContent className="flex-1 pb-4">
                  <div className="mt-4 flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1.5" title="Limite de taxa (msgs/min)">
                      <Activity className="h-4 w-4" />
                      {project.message_throttle_per_minute != null ? (
                        <span>{project.message_throttle_per_minute} <span className="text-xs">msgs/min</span></span>
                      ) : (
                        <span>Sem limite</span>
                      )}
                    </div>
                  </div>
                  <div className="mt-3 space-y-1 text-xs text-muted-foreground">
                    <p>{project.facebook_credentials_configured ? "Messenger configurado" : "Messenger pendente"}</p>
                    {project.facebook_page_id ? (
                      <p>
                        Page ID: <span className="font-mono">{project.facebook_page_id}</span>
                      </p>
                    ) : null}
                  </div>
                </CardContent>
                <CardFooter className="pt-0">
                  <Button
                    variant={isCurrent ? "secondary" : "outline-solid"}
                    className="w-full"
                    onClick={() => navigate(`/${project.slug}`)}
                  >
                    {isCurrent ? "Ir para o Painel" : "Abrir Projeto"}
                  </Button>
                </CardFooter>
              </Card>
            )
          })
        )}
      </div>
    </div>
  )
}

export default ProjectsPage
